import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/api/queryKeys';
import type { AssignableJobsCreatedSort } from '@/types';
import { commandCentre } from '../CommandCentre';
import {
  DEFAULT_TRACKING_THRESHOLDS,
  TrackingThresholds,
} from '@/modules/commandCentre/utils/operatorTracking';

export const useCommandCentreConfig = () =>
  useQuery({
    queryKey: [QUERY_KEYS.commandCentreConfig],
    queryFn: async () => (await commandCentre.getConfig()).data,
    staleTime: Infinity,
  });

export const useCommandCentreTrackingThresholds = (): TrackingThresholds => {
  const { data } = useCommandCentreConfig();
  if (!data) return DEFAULT_TRACKING_THRESHOLDS;
  return {
    offlineSeconds: data.offlineSeconds,
    linkHealthGoodSeconds: data.linkHealthGoodSeconds,
    decayTickMs: data.decayTickMs,
    awayTimeoutMinutes:
      data.awayTimeoutMinutes ?? DEFAULT_TRACKING_THRESHOLDS.awayTimeoutMinutes,
  };
};

export const useCommandCentreSites = () =>
  useQuery({
    queryKey: [QUERY_KEYS.commandCentreSites],
    queryFn: async () => (await commandCentre.getSites()).data ?? [],
  });

export const useCommandCentreOperators = (siteId?: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.commandCentreOperators, siteId],
    queryFn: async () => (await commandCentre.getOperators(siteId!)).data ?? [],
    enabled: !!siteId,
  });

export const useCommandCentrePositions = (siteId?: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.commandCentrePositions, siteId],
    queryFn: async () => (await commandCentre.getPositions(siteId!)).data ?? [],
    enabled: !!siteId,
  });

export const useCommandCentreEvents = (
  siteId?: number,
  page = 1,
  types: string[] = [],
) =>
  useQuery({
    queryKey: [QUERY_KEYS.commandCentreEvents, siteId, page, types],
    queryFn: async () =>
      (await commandCentre.getEvents(siteId!, page, 20, types)).data,
    enabled: !!siteId,
  });

export const useCommandCentreCameras = (siteId?: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.commandCentreCameras, siteId],
    queryFn: async () => (await commandCentre.getCameras(siteId!)).data ?? [],
    enabled: !!siteId,
  });

export const useCommandCentreCameraStream = (cameraId?: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.commandCentreCameraStream, cameraId],
    queryFn: async () => (await commandCentre.getCameraStream(cameraId!)).data,
    enabled: !!cameraId,
  });

export const useCommandCentreOperator = (userId?: number, siteId?: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.commandCentreOperator, userId, siteId],
    queryFn: async () =>
      (await commandCentre.getOperator(userId!, siteId)).data,
    enabled: !!userId,
  });

export const useCommandCentreAssignableJobs = (
  siteId: number | undefined,
  search: string,
  jobType: string | undefined,
  createdSort: AssignableJobsCreatedSort,
  page: number,
  enabled: boolean,
) =>
  useQuery({
    queryKey: [
      QUERY_KEYS.commandCentreAssignableJobs,
      siteId,
      search,
      jobType,
      createdSort,
      page,
    ],
    queryFn: async () =>
      (
        await commandCentre.getAssignableJobs(
          siteId!,
          search,
          jobType,
          createdSort,
          page,
          10,
        )
      ).data,
    enabled: enabled && !!siteId,
    placeholderData: keepPreviousData,
  });

export const useAssignCommandCentreJob = () =>
  useMutation({ mutationFn: commandCentre.assignJob });
